import { Container, Rectangle, Sprite, Texture } from "pixi.js";
import type { GridCells, GridEffect, Log, PresentationTaskRunner } from "@slot/presentation";
import type { ReelPaylineTuning } from "./ReelWinTuning";

/**
 * The stroke's art: a needle in three slices, each drawn at its native aspect `thicknessPixels` tall. The frames must be
 * untrimmed and unrotated (the draw crops a slice's frame to reveal it). A missing cap leaves the middle running to
 * that tip.
 */
export interface ReelPaylineArt {
  /** At a line's left tip. */
  readonly start?: Texture | null;
  /** Tiled along every straight part. */
  readonly mid: Texture;
  /** At a line's right tip, over the middle. */
  readonly end?: Texture | null;
}

type Point = { x: number; y: number };

/** A straight run of a stroke, in the parent's space, left to right, with the caps its tips take. */
interface StraightPart {
  a: Point;
  b: Point;
  capStart: boolean;
  capEnd: boolean;
}

/** One slice at native scale, revealed by cropping its own texture's frame to the drawn length. */
interface Segment {
  sprite: Sprite;
  texture: Texture;
  /** Part-local left edge, and where its reveal stops (the next slice's edge, or the part's end). */
  left: number;
  segEnd: number;
  /** The slice's full width, in the parent's units. */
  width: number;
  frameWidth: number;
}

interface DrawnPart {
  root: Container;
  segments: Segment[];
  length: number;
  xa: number;
  xb: number;
}

/** What the stroke reads from the grid: where each winning cell sits. */
export type ReelPaylineGrid = Pick<GridCells, "gridRoot" | "gridColumnCount" | "maskToIndices" | "getCellLocalPosition">;

/**
 * The payline stroke through a win's cells, a grid effect (the Cocos template's `ReelPaylineLayer` +
 * `NeedlePaylineCoordinator`, decision 64). Driven by the win highlight, not the frame: a title passes `drawLines` as
 * the highlight's `onLineHighlighted`, so a win's needle, dim, winners and label appear together, on the highlight's
 * own beat. A win group's lines arrive together and draw as their union: a segment two lines share is drawn once, the
 * cells they share without a segment stay a crossing, and every line's tip is capped. A single line is split at its
 * corners and capped at its two tips. The capped tips reach `endOverhangPixels` past their cells.
 *
 * The draw is one front sweeping left to right across the reels in `expandSeconds` (a pause at each corner with
 * `staggerSeconds`): every slice is fixed in place at its native size and its frame is cropped as the front crosses
 * it, so nothing stretches and the last middle slice stops flush at the end cap. The draw runs in the caller's scope
 * (a slam finishes it drawn); the hold and the fade run on unscoped, then the stroke is gone. A new spin drops every
 * stroke, mid-draw included (`clearForNewSpin`). The strokes live in `parent` (a title's layer under the symbols).
 */
export class ReelPaylineEffect implements GridEffect {
  private readonly grid: ReelPaylineGrid;
  private readonly parent: Container;
  private readonly art: ReelPaylineArt | null;
  private readonly tuning: ReelPaylineTuning;
  private readonly runner: PresentationTaskRunner;
  private readonly log: Log;
  private readonly active = new Set<DrawnPart>();
  /** Bumped by each teardown: a draw in flight stops. */
  private generation = 0;

  public constructor(input: {
    grid: ReelPaylineGrid;
    /** Where the strokes are drawn (slot-bloody-teddy: the grid's FX underlay, under the symbols). */
    parent: Container;
    /** Null: no stroke (logged once). */
    art: ReelPaylineArt | null;
    tuning: ReelPaylineTuning;
    taskRunner: PresentationTaskRunner;
    log: Log;
  }) {
    this.grid = input.grid;
    this.parent = input.parent;
    this.art = input.art;
    this.tuning = input.tuning;
    this.runner = input.taskRunner;
    this.log = input.log;
    if (!this.art) this.log("[ReelPaylineEffect] no needle art — paylines are not drawn");
  }

  public removeSymbolsByMask(): Promise<void> {
    return Promise.resolve();
  }

  /** Every stroke goes at once, mid-draw included. */
  public clearForNewSpin(): void {
    this.generation += 1;
    for (const part of this.active) destroyPart(part);
    this.active.clear();
  }

  /**
   * One line, or a win group's lines as their union; fire-and-forget. `scopeId` (the first pass's win scope) scopes the
   * draw; a re-show without one (an idle cycle) draws unscoped.
   */
  public drawLines(lineMasks: readonly string[], scopeId?: string): void {
    if (!this.art || lineMasks.length === 0) return;
    const parts = lineMasks.length === 1 ? this.lineParts(lineMasks[0]!) : this.unionParts(lineMasks);
    if (parts.length === 0) return;
    this.applyEndOverhang(parts);
    void this.run(parts, this.generation, scopeId);
  }

  // ── geometry ─────────────────────────────────────────────────────────────────

  /** One line's cell centres, left to right, merged into its straight parts; its two tips capped. */
  private lineParts(mask: string): StraightPart[] {
    const points = this.cellIndices(mask)
      .map((index) => this.grid.getCellLocalPosition(index))
      .sort((p, q) => p.x - q.x || p.y - q.y)
      .map((point) => this.toParent(point));
    if (points.length < 2) return [];
    const parts: StraightPart[] = [];
    let start = 0;
    for (let i = 1; i < points.length - 1; i++) {
      if (isCorner(points[i - 1]!, points[i]!, points[i + 1]!)) {
        parts.push({ a: points[start]!, b: points[i]!, capStart: false, capEnd: false });
        start = i;
      }
    }
    parts.push({ a: points[start]!, b: points[points.length - 1]!, capStart: false, capEnd: false });
    parts[0]!.capStart = true;
    parts[parts.length - 1]!.capEnd = true;
    return parts;
  }

  /**
   * A group's lines as one stroke, in grid (column, row) space: each line's consecutive cells are edges one column
   * apart; an edge two lines share is kept once; runs of equal slope merge into straight parts, ordered left to right. A
   * cell whose edges all go right is a start tip (capped), all left an end tip (capped); a junction takes no cap.
   */
  private unionParts(masks: readonly string[]): StraightPart[] {
    const cols = this.grid.gridColumnCount;
    const col = (index: number): number => index % cols;
    const row = (index: number): number => Math.floor(index / cols);
    const edges = new Map<string, { left: number; right: number }>();
    for (const mask of masks) {
      const ordered = this.cellIndices(mask).sort((a, b) => col(a) - col(b));
      for (let i = 0; i + 1 < ordered.length; i++) {
        const [left, right] = col(ordered[i]!) <= col(ordered[i + 1]!) ? [ordered[i]!, ordered[i + 1]!] : [ordered[i + 1]!, ordered[i]!];
        edges.set(`${left}-${right}`, { left, right });
      }
    }
    if (edges.size === 0) return [];
    const runs: number[][] = [...edges.values()].map((edge) => [edge.left, edge.right]);
    const slope = (run: number[]): number => row(run[1]!) - row(run[0]!);
    for (let merged = true; merged; ) {
      merged = false;
      for (let i = 0; i < runs.length && !merged; i++) {
        for (let j = 0; j < runs.length; j++) {
          if (i === j || slope(runs[i]!) !== slope(runs[j]!) || runs[i]![runs[i]!.length - 1] !== runs[j]![0]) continue;
          runs[i] = runs[i]!.concat(runs[j]!.slice(1));
          runs.splice(j, 1);
          merged = true;
          break;
        }
      }
    }
    const hasRight = new Set([...edges.values()].map((edge) => edge.left));
    const hasLeft = new Set([...edges.values()].map((edge) => edge.right));
    runs.sort((p, q) => col(p[0]!) - col(q[0]!));
    return runs.map((run) => {
      const first = run[0]!;
      const last = run[run.length - 1]!;
      return {
        a: this.toParent(this.grid.getCellLocalPosition(first)),
        b: this.toParent(this.grid.getCellLocalPosition(last)),
        capStart: hasRight.has(first) && !hasLeft.has(first),
        capEnd: hasLeft.has(last) && !hasRight.has(last),
      };
    });
  }

  /** Each capped tip moves `endOverhangPixels` away from its part's other end (negative: inward). */
  private applyEndOverhang(parts: StraightPart[]): void {
    const overhang = this.tuning.endOverhangPixels;
    if (!overhang) return;
    for (const part of parts) {
      if (part.capStart) part.a = movedAwayFrom(part.a, part.b, overhang);
      if (part.capEnd) part.b = movedAwayFrom(part.b, part.a, overhang);
    }
  }

  private cellIndices(mask: string): number[] {
    try {
      return this.grid.maskToIndices(mask);
    } catch (error) {
      this.log(`[ReelPaylineEffect] bad line mask (${String(error)})`);
      return [];
    }
  }

  private toParent(point: Point): Point {
    const root = this.grid.gridRoot;
    if (!root) return { x: point.x, y: point.y };
    const local = this.parent.toLocal(point, root);
    return { x: local.x, y: local.y };
  }

  // ── the draw ─────────────────────────────────────────────────────────────────

  /** Lead-in, the left-to-right sweep (scoped), the hold and the fade (unscoped), then gone. */
  private async run(parts: StraightPart[], generation: number, scopeId?: string): Promise<void> {
    const t = this.tuning;
    const drawn: DrawnPart[] = [];
    try {
      if (t.startDelaySeconds > 0) {
        await this.runner.sleep(t.startDelaySeconds, scopeId);
        if (generation !== this.generation) return;
      }
      for (const part of parts) {
        const spawned = this.spawn(part);
        drawn.push(spawned);
        this.active.add(spawned);
      }
      const xs = drawn.flatMap((part) => [part.xa, part.xb]);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const spanX = Math.max(0.0001, maxX - minX);
      const sweepTo = (frontX: number): void => {
        for (const part of drawn) {
          const dx = part.xb - part.xa;
          const k = dx > 0.0001 ? (frontX - part.xa) / dx : frontX >= part.xa ? 1 : 0;
          reveal(part, Math.max(0, Math.min(1, k)) * part.length);
        }
      };
      sweepTo(minX);
      const bounds = [minX, ...(t.staggerSeconds > 0 ? cornerXs(parts) : []), maxX];
      for (let k = 0; k + 1 < bounds.length; k++) {
        const x0 = bounds[k]!;
        const x1 = bounds[k + 1]!;
        await this.runner.tweenProgress(Math.max(0.001, t.expandSeconds * ((x1 - x0) / spanX)), (u) => sweepTo(x0 + (x1 - x0) * u), scopeId, { easing: t.expandEasing });
        if (generation !== this.generation) return;
        sweepTo(x1);
        if (k + 2 < bounds.length) {
          await this.runner.sleep(t.staggerSeconds, scopeId);
          if (generation !== this.generation) return;
        }
      }
      sweepTo(maxX);
      // The exit runs unscoped: after a slam has finished the draw, the stroke still holds and fades.
      await this.runner.sleep(t.holdSeconds);
      if (generation !== this.generation) return;
      await this.runner.tweenProgress(
        Math.max(0.001, t.fadeOutSeconds),
        (u) => {
          for (const part of drawn) if (!part.root.destroyed) part.root.alpha = 1 - u;
        },
        undefined,
        { easing: t.fadeEasing },
      );
    } finally {
      for (const part of drawn) {
        this.active.delete(part);
        destroyPart(part);
      }
    }
  }

  /** One straight part: its root at the part's start, turned along it, and its slices laid out hidden. */
  private spawn(part: StraightPart): DrawnPart {
    const art = this.art!;
    const length = Math.hypot(part.b.x - part.a.x, part.b.y - part.a.y);
    const root = new Container({ label: "PaylineStroke" });
    root.position.set(part.a.x, part.a.y);
    root.rotation = Math.atan2(part.b.y - part.a.y, part.b.x - part.a.x);
    this.parent.addChild(root);

    const thickness = this.tuning.thicknessPixels;
    const segments: Segment[] = [];
    const add = (texture: Texture, label: string, left: number, segEnd: number): number => {
      const segment = slice(texture, label, left, segEnd, thickness);
      root.addChild(segment.sprite);
      segments.push(segment);
      return segment.width;
    };
    const start = part.capStart ? (art.start ?? null) : null;
    const end = part.capEnd ? (art.end ?? null) : null;
    const startWidth = start ? add(start, "Start", 0, sliceWidth(start, thickness)) : 0;
    const endWidth = end ? sliceWidth(end, thickness) : 0;
    const midEnd = end ? Math.max(startWidth, length - endWidth) : length;
    const midWidth = sliceWidth(art.mid, thickness);
    for (let x = startWidth; midWidth > 0 && x < midEnd - 0.5; x += midWidth) add(art.mid, "Mid", x, Math.min(x + midWidth, midEnd));
    if (end) add(end, "End", Math.max(0, length - endWidth), length);
    return { root, segments, length, xa: part.a.x, xb: part.b.x };
  }
}

/** A slice's width at `thickness` tall, its frame's aspect kept. */
function sliceWidth(texture: Texture, thickness: number): number {
  return texture.frame.height > 0 ? thickness * (texture.frame.width / texture.frame.height) : 0;
}

/** A hidden slice at `left`, anchored on its left edge, at native scale; its own texture, so its frame can be cropped. */
function slice(base: Texture, label: string, left: number, segEnd: number, thickness: number): Segment {
  const { frame, orig } = base;
  const texture = new Texture({ source: base.source, frame: new Rectangle(frame.x, frame.y, frame.width, frame.height), orig: new Rectangle(orig.x, orig.y, orig.width, orig.height), dynamic: true });
  const sprite = new Sprite({ texture, label, anchor: { x: 0, y: 0.5 } });
  sprite.scale.set(base.frame.height > 0 ? thickness / base.frame.height : 1);
  sprite.position.set(left, 0);
  sprite.visible = false;
  return { sprite, texture, left, segEnd, width: sliceWidth(base, thickness), frameWidth: base.frame.width };
}

/** Each slice shows the part of it left of `front` (and of its own stop), cropped at native scale. */
function reveal(part: DrawnPart, front: number): void {
  for (const segment of part.segments) {
    if (segment.sprite.destroyed) continue;
    const shown = segment.width > 0 ? Math.max(0, Math.min(1, (Math.min(front, segment.segEnd) - segment.left) / segment.width)) : 0;
    segment.sprite.visible = shown > 0;
    if (shown <= 0) continue;
    const width = segment.frameWidth * shown;
    if (segment.texture.frame.width === width) continue;
    segment.texture.frame.width = width;
    segment.texture.orig.width = width;
    segment.texture.update();
  }
}

function destroyPart(part: DrawnPart): void {
  if (part.root.destroyed) return;
  for (const segment of part.segments) segment.texture.destroy(false);
  part.root.destroy({ children: true });
}

/** True when the direction turns at `q` (the cross product of p→q and q→r, with a pixel tolerance). */
function isCorner(p: Point, q: Point, r: Point): boolean {
  return Math.abs((q.x - p.x) * (r.y - q.y) - (q.y - p.y) * (r.x - q.x)) > 0.5;
}

/** `point` moved `distance` straight away from `anchor`. */
function movedAwayFrom(point: Point, anchor: Point, distance: number): Point {
  const dx = point.x - anchor.x;
  const dy = point.y - anchor.y;
  const length = Math.hypot(dx, dy);
  if (length < 0.0001) return point;
  return { x: point.x + (dx / length) * distance, y: point.y + (dy / length) * distance };
}

/** Where the sweep pauses: the part ends that take no cap (corners and junctions), left to right, once each. */
function cornerXs(parts: readonly StraightPart[]): number[] {
  const xs = parts.flatMap((part) => [...(part.capStart ? [] : [part.a.x]), ...(part.capEnd ? [] : [part.b.x])]).sort((a, b) => a - b);
  const out: number[] = [];
  for (const x of xs) if (out.length === 0 || Math.abs(x - out[out.length - 1]!) > 0.5) out.push(x);
  return out;
}
